import { useState } from 'react';
import { ApolloQueryResult } from '@apollo/client';

import {
  AdCampaignMediaTypeEnum,
  AdCampaignPureType,
  AdCampaignQueryFilter,
  AdCampaignQueryPagination,
  AdCampaignQuerySort,
  AdCampaignShortTypeFragment,
  AdCampaignStatusesEnum,
  AdCampaignsStatusesPureType,
  AdCampaignTypeEnum,
  InputMaybe,
  MediaAdCampaignsPaginationQueryQuery,
  PaginationInfoType,
  ReplicationRole,
  useMediaAdCampaignsPaginationQueryQuery,
} from '@shared/api/graphql/generated';
import { BaseQueryRefetchResultType, FetchPolicyKeys } from '@shared/api/graphql/types';

export type AdCampaignPreviewType = Pick<
  AdCampaignPureType,
  'id' | 'title' | 'timingSettings' | 'enable' | 'type' | 'replicationRole' | 'mediaType'
> & {
  tags?: Array<{ id: string; title: string }> | null;
  points: { id: string }[];
  promos: { id: string }[];
};

export type UseAdCampaignsResultType = BaseQueryRefetchResultType & {
  called: boolean;
  data: {
    adCampaigns?: AdCampaignShortTypeFragment[] | null;
    statusesInfo?: AdCampaignsStatusesPureType | null;
    paginationInfo?: PaginationInfoType | null;
  };
  loadMore: (
    filters: AdCampaignQueryFilter,
    pagination: AdCampaignQueryPagination,
    sort: AdCampaignQuerySort
  ) => Promise<ApolloQueryResult<MediaAdCampaignsPaginationQueryQuery>>;
};

export type UseAdCampaignsType = [
  request: (filters: AdCampaignQueryFilter, pagination: AdCampaignQueryPagination, sort: AdCampaignQuerySort) => void,
  result: UseAdCampaignsResultType,
];

type AdCampaignsQueryState = {
  query?: string | null;
  companyIds: InputMaybe<string>[];
  companyTagIds: InputMaybe<string>[];
  statuses: InputMaybe<AdCampaignStatusesEnum>[];
  replicationRoles: ReplicationRole[];
  pointId?: string | null;
  type: InputMaybe<AdCampaignTypeEnum>[];
  page: number;
  pageSize: number;
  sort: AdCampaignQuerySort;
  skip: boolean;
  mediaType?: InputMaybe<AdCampaignMediaTypeEnum>[];
};

const DEFAULT_REPLICATION_ROLES = [ReplicationRole.None, ReplicationRole.Slave];

const useAdCampaigns = (): UseAdCampaignsType => {
  const [queryState, setQueryState] = useState<AdCampaignsQueryState>({
    query: '',
    companyIds: [],
    companyTagIds: [],
    statuses: [],
    type: [],
    replicationRoles: DEFAULT_REPLICATION_ROLES,
    pointId: '',
    page: 0,
    pageSize: 0,
    sort: AdCampaignQuerySort.IdDesc,
    skip: true,
    mediaType: undefined,
  });

  const { data, called, loading, error, networkStatus, fetchMore, refetch } = useMediaAdCampaignsPaginationQueryQuery({
    variables: {
      filters: {
        query: queryState.query,
        companyIds: queryState.companyIds,
        companyTagIds: queryState.companyTagIds as string[],
        statuses: queryState.statuses,
        pointId: queryState.pointId,
        type: queryState.type,
        replicationRoles: queryState.replicationRoles,
        mediaType: queryState.mediaType,
      },
      pagination: { page: queryState.page, pageSize: queryState.pageSize },
      sort: queryState.sort,
    },
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
    notifyOnNetworkStatusChange: true,
    skip: queryState.skip,
  });

  const request = (
    filters: AdCampaignQueryFilter,
    pagination: AdCampaignQueryPagination,
    sort: AdCampaignQuerySort
  ) => {
    setQueryState({
      query: filters.query,
      companyIds: filters.companyIds || [],
      companyTagIds: filters.companyTagIds || [],
      statuses: filters.statuses || [],
      pointId: filters.pointId,
      type: filters.type || [],
      replicationRoles: filters.replicationRoles || DEFAULT_REPLICATION_ROLES,
      mediaType: filters.mediaType || undefined,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort,
      skip: false,
    });
  };

  async function loadMore(
    filters: AdCampaignQueryFilter,
    pagination: AdCampaignQueryPagination,
    sort: AdCampaignQuerySort
  ) {
    const result = await fetchMore({
      variables: {
        filters,
        pagination,
        sort,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        const data = {
          ...prev,
          adcampaignsPaginationQuery: {
            ...prev?.adcampaignsPaginationQuery,
            ...fetchMoreResult?.adcampaignsPaginationQuery,
            result: [
              ...(prev?.adcampaignsPaginationQuery?.result || []),
              ...(fetchMoreResult?.adcampaignsPaginationQuery?.result || []),
            ],
          },
        };

        return data;
      },
    });

    return result;
  }

  const result: UseAdCampaignsResultType = {
    error,
    loading,
    called,
    networkStatus,
    data: {
      adCampaigns: data?.adcampaignsPaginationQuery.result,
      statusesInfo: data?.adcampaignsPaginationQuery.statusesInfo,
      paginationInfo: data?.adcampaignsPaginationQuery.paginationInfo,
    },
    loadMore,
    refetch,
  };

  return [request, result];
};

export default useAdCampaigns;
